// Deterministic step generator for the Scenarios "guided session" flow.
//
// Given a scenario's content (words / rules / constructions), produces an
// ordered list of step descriptors the SessionController iterates through.
// Pure: same input → same output. No DB, no randomness, no time.
//
// Why pure-and-deterministic? The session can be resumed across devices
// without persisting the step list — both clients regenerate the same
// sequence from the same scenario payload. Step `index` is positional.
//
// Algorithm version is stamped onto the run (see `SCENARIO_STEPS_VERSION`)
// so future tweaks don't surprise mid-flight users.

// v2: dropped MINI_CHECK auto-injection + renamed LINE → CHUNK. The
// session is now strictly "study + light practice" (recognition);
// real production happens at the checkpoint. Each RULE step can carry
// its own inline 2–3 example MCQ via the rule payload — so the
// "after every 3 content steps, run a quiz" interleave is no longer
// needed. CHUNK is the rename of LINE: same authoring payload, but
// the renderer shows RO + audio with tap-to-reveal RU instead of
// asking the learner to type the translation.
export const SCENARIO_STEPS_VERSION = 2;

// Words are batched 3 at a time; the last batch may be 1–3.
const WORD_BATCH_SIZE = 3;

export type StepDescriptor =
  | { kind: "WORD"; lemmas: string[] }
  | { kind: "RULE"; ruleSlug: string }
  | { kind: "CHUNK"; constructionId: string };

export type ScenarioStepsInput = {
  // Lemmas in authoring order (`ScenarioWord.position` ascending).
  wordLemmas: readonly string[];
  // Rule slugs in authoring order.
  ruleSlugs: readonly string[];
  // Construction ids in authoring order.
  constructionIds: readonly string[];
};

/**
 * Generate the ordered step sequence for a scenario session.
 *
 * Strategy (v2):
 *   1. Batch words into groups of 3 (last may be 1–3).
 *   2. Round-robin interleave WORD batches, RULES, CHUNKS so the
 *      user never spends >2 consecutive steps on the same surface.
 *   3. No MINI_CHECK injection — practice is owned by RULE steps
 *      (inline MCQ on rule examples, see `GrammarRule.examples`)
 *      and the final checkpoint quiz.
 *
 * Edge cases:
 *   - 0 content (empty arrays) → returns [].
 *   - 1 content item → that item alone.
 */
export function generateScenarioSteps(input: ScenarioStepsInput): StepDescriptor[] {
  const wordBatches = batchWords(input.wordLemmas);
  const wordSteps: StepDescriptor[] = wordBatches.map((lemmas) => ({ kind: "WORD", lemmas }));
  const ruleSteps: StepDescriptor[] = input.ruleSlugs.map((ruleSlug) => ({ kind: "RULE", ruleSlug }));
  const chunkSteps: StepDescriptor[] = input.constructionIds.map((constructionId) => ({
    kind: "CHUNK",
    constructionId,
  }));

  return roundRobin([wordSteps, ruleSteps, chunkSteps]);
}

/**
 * Slice an ordered list of lemmas into batches of `WORD_BATCH_SIZE`.
 * The last batch is the remainder (length 1–3); empty input → no batches.
 *
 * Order is preserved: the author's `position` ordering survives all the
 * way through to the WORD step the learner sees.
 */
function batchWords(lemmas: readonly string[]): string[][] {
  const out: string[][] = [];
  for (let i = 0; i < lemmas.length; i += WORD_BATCH_SIZE) {
    out.push(lemmas.slice(i, i + WORD_BATCH_SIZE));
  }
  return out;
}

/**
 * Round-robin merge: pull one item from each list in turn, skipping
 * empties, until all are drained. Result interleaves surfaces evenly
 * even when one list is much longer than the others.
 */
function roundRobin<T>(lists: T[][]): T[] {
  const out: T[] = [];
  const cursors = lists.map(() => 0);
  while (true) {
    let advanced = false;
    for (let l = 0; l < lists.length; l++) {
      const list = lists[l]!;
      const idx = cursors[l]!;
      if (idx < list.length) {
        out.push(list[idx]!);
        cursors[l] = idx + 1;
        advanced = true;
      }
    }
    if (!advanced) break;
  }
  return out;
}
